<script lang="ts" setup>
import type { NavItem } from '~kui/types/navigation';
import type { ComponentPublicInstance } from 'vue';

import { useScrollLock } from '@vueuse/core';
import { Icon } from '~ds/components/ui/icon';
import { Sheet, SheetTitle, SheetTrigger } from '~ds/components/ui/sheet';
import { ICONS } from '~ds/lib/icons';
import { useMotionAllowed } from '~kui/composables/useMotionPresets';
import { DialogContent, DialogPortal } from 'reka-ui';

/** `animationsEnabled` is the student's in-app motion switch, on top of the OS preference. */
interface Props {
  navItems: NavItem[];
  animationsEnabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  animationsEnabled: true,
});

const emit = defineEmits<{
  itemClick: [item: NavItem];
}>();

const open = defineModel<boolean>('open', { default: false });

const motionAllowed = useMotionAllowed(() => props.animationsEnabled);

const panelClasses = computed(() => twMerge(cva('fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background', {
  variants: {
    motion: {
      true: 'duration-250 ease-out data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-top-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-4',
      false: '',
    },
  },
})({
  motion: motionAllowed.value,
})));

const closeButton = useTemplateRef<ComponentPublicInstance>('closeButton');

const focusCloseButton = (event: Event) => {
  event.preventDefault();
  (closeButton.value?.$el as HTMLElement | undefined)?.focus();
};

const isBodyScrollLocked = useScrollLock(import.meta.client ? document.body : null);

watch(open, (value) => {
  isBodyScrollLocked.value = value;
}, { immediate: true });
</script>

<template>
  <!-- The panel is reka's DialogContent rather than the DS SheetContent: SheetContent always -->
  <!-- renders its own small close button and a dimming overlay, and this drawer is a full-screen -->
  <!-- page with its own close control. -->
  <Sheet v-model:open="open">
    <SheetTrigger v-if="$slots.trigger" as-child>
      <slot name="trigger" />
    </SheetTrigger>
    <DialogPortal>
      <DialogContent
        :class="panelClasses"
        aria-modal="true"
        :aria-describedby="undefined"
        data-testid="unified-navigation-drawer"
        @open-auto-focus="focusCloseButton"
      >
        <SheetTitle class="sr-only">
          {{ $t('unified.nav.drawerTitle') }}
        </SheetTitle>
        <KUIUnifiedLayoutContainer fill>
          <div class="flex items-center justify-end gap-4 pt-6 md:pt-7 pb-4 md:pr-4" data-testid="unified-navigation-drawer-top-row">
            <div v-if="$slots.account" class="min-w-0 flex-1">
              <slot name="account" />
            </div>
            <KUICoreClickable
              ref="closeButton"
              class="flex shrink-0 justify-center items-center rounded-full cursor-pointer size-10 bg-white"
              :aria-label="$t('unified.nav.closeMenu')"
              @click="open = false"
            >
              <Icon :name="ICONS.cancel" size="lg" />
            </KUICoreClickable>
          </div>

          <nav :aria-label="$t('unified.nav.mainAriaLabel')">
            <ul role="list" class="flex flex-col gap-1 py-4">
              <li
                v-for="item in navItems"
                :key="item.label"
                class="-mx-4 px-8 py-2"
                :class="item.isActive ? 'bg-card' : ''"
              >
                <KUICoreMenuItem
                  :item="item"
                  v-bind="item.isActive ? { 'aria-current': 'page' } : {}"
                  @click="emit('itemClick', item)"
                />
              </li>
            </ul>
          </nav>
          <div class="flex flex-col pb-8">
            <slot />
          </div>
        </KUIUnifiedLayoutContainer>
      </DialogContent>
    </DialogPortal>
  </Sheet>
</template>
